/**
 * «شهرِ دیفالتِ این آدم چیه؟» — یه‌بار، سرِ بالا آمدنِ اپ و هر بار که نشست
 * عوض می‌شه.
 *
 * سه منبع، به همین ترتیب:
 *
 *   ۱. **پروفایل.** کسی که وارد شده خودش انتخاب کرده (تنظیمات ← شهر)؛ هر
 *      حدسی روی این حرفِ صریح، حدسِ اضافه‌ست.
 *   ۲. **IP** — فقط وقتی اپ پورتش رو داده باشه (یعنی وب، و فقط با پیکربندی)
 *      و کسی وارد نشده. نگاه کن به ports.ts و lib/ipCity.
 *      خروجیش با فهرستِ شهرهای اپ جور می‌شه: خودِ اون شهر اگه هست، وگرنه
 *      نزدیک‌ترین شهری که محتوا داره.
 *   ۳. **اولین شهرِ اپ.** ته‌خطِ همه‌ی مسیرها. صفحه‌ای که هیچ شهری نداره
 *      فهرستش خالیه، و خالی خونده می‌شه «چیزی نیست» — نه «هنوز نمی‌دونم
 *      کجایی».
 *
 * این‌جا (data) می‌شینه نه (lib): به شبکه دست می‌زنه. `lib/cityScope` عمداً
 * خالص مونده تا تستش بدونِ هیچ بک‌اندی اجرا شه.
 */
import { ipLocation } from '../platform';
import { activeCity, adoptHomeCity, homeCity, matchCity } from '../lib/cityScope';
import { reportError } from '../lib/report';
import { cityRepo } from './cityRepo';
import type { AppCity, Profile } from '../domain/types';

/**
 * شهرِ دیفالت رو معلوم می‌کنه و توی `cityScope` می‌ذاره.
 *
 * بی‌صدا شکست می‌خوره: شبکه‌ی قطع یعنی همون شهرِ ذخیره‌شده‌ی روی دستگاه
 * می‌مونه (نگاه کن به initCityScope)، نه یه اپِ بی‌شهر.
 *
 * همه‌جا `adoptHomeCity` نه `setHomeCity`: این‌جا هیچ‌کس روی دکمه‌ای نزده.
 * آشتیِ خودکارِ دیفالت حق نداره شهرِ درحالِ‌تماشا (انتخابِ صریحِ کاربر از
 * دکمه‌ی کنارِ جستجو) رو پاک کنه — با setHomeCity، مهمانِ وب سرِ هر رفرش
 * انتخابش رو به حدسِ IP می‌باخت.
 */
export async function resolveHomeCity(profile: Profile | null): Promise<AppCity | null> {
  try {
    if (profile?.cityId) {
      // همون شهری که از قبل روی دستگاه نشسته؟ پس هیچ رفت‌وبرگشتی لازم نیست.
      const known = homeCity();
      // adopt بی‌هزینه است وقتی چیزی عوض نشده — ولی حالتِ «فراموش‌شده»ی
      // بعد از حذفِ حساب را تمام می‌کند (نگاه کن به forgetCity).
      if (known?.id === profile.cityId) { adoptHomeCity(known); return known; }
      const found = await cityRepo.byId(profile.cityId);
      if (found) { adoptHomeCity(found); return found; }
    }

    // کاربرِ واردشده‌ای که شهر انتخاب نکرده، از IP حدس زده نمی‌شه: شهرش یه
    // فیلدِ خالیه که خودش پرش می‌کنه، نه یه چیزِ ناشناخته.
    const cities = await cityRepo.list();
    const lookup = ipLocation();
    if (!profile && lookup) {
      const found = await lookup();
      const guess = found ? matchCity(found, cities) : null;
      if (guess) { adoptHomeCity(guess, { guess: true }); return guess; }
    }

    // شهرِ ذخیره‌شده‌ی روی دستگاه هنوز معتبره؟ پس دست بهش نمی‌زنیم — کسی که
    // دیروز تورنتو رو دیده، امروز نباید اتاوا تحویل بگیره.
    const saved = homeCity();
    if (saved && cities.some((c) => c.id === saved.id)) return saved;

    const first = cities[0] ?? null;
    if (first) adoptHomeCity(first, { guess: true });
    return first;
  } catch (e) {
    reportError('cityBoot.resolveHome', e);
    return activeCity();
  }
}
